import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Play, Loader2, RefreshCw, Database, Activity, Clock, 
  CheckCircle2, AlertCircle, TestTube, RotateCcw, TrendingUp,
  Calendar, Settings, History, XCircle, BarChart3, Timer, Zap,
  Search, ArrowUpRight, ArrowDownRight, Minus
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const StockPerfPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [schedulerState, setSchedulerState] = useState(null);
  
  // Config state
  const [config, setConfig] = useState({});
  const [configDirty, setConfigDirty] = useState(false);
  
  // Test run config
  const [testSymbolsLimit, setTestSymbolsLimit] = useState(50);
  
  // Run history
  const [runs, setRuns] = useState([]);
  const [failedSymbols, setFailedSymbols] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [showFailedDialog, setShowFailedDialog] = useState(false);
  
  // Stock lookup state
  const [lookupSymbol, setLookupSymbol] = useState("");
  const [lookupData, setLookupData] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);

  // Polling for live updates
  useEffect(() => {
    fetchStats();
    fetchSchedulerState();
    const interval = setInterval(() => {
      fetchStats();
      fetchSchedulerState();
    }, 5000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (activeTab === "history") {
      fetchRuns();
    }
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/stock-perf/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        setConfig(data.config || {});
        setRunning(data.state?.status === "running");
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerState = async () => {
    try {
      const response = await fetch(`${API}/stock-perf/scheduler-state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSchedulerState(data);
      }
    } catch (error) {
      console.error("Failed to fetch scheduler state:", error);
    }
  };

  const fetchRuns = async () => {
    try {
      const response = await fetch(`${API}/stock-perf/runs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRuns(data.runs || []);
      }
    } catch (error) {
      console.error("Failed to fetch runs:", error);
    }
  };

  const fetchFailedSymbols = async (runId) => {
    try {
      const response = await fetch(`${API}/stock-perf/runs/${runId}/failed`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setFailedSymbols(data.failed || []);
        setSelectedRunId(runId);
        setShowFailedDialog(true);  // Open the dialog
      }
    } catch (error) {
      console.error("Failed to fetch failed symbols:", error);
      toast.error("Failed to load failed symbols");
    }
  };

  const lookupStock = async (e) => {
    e?.preventDefault();
    if (!lookupSymbol.trim()) return;
    
    setLookupLoading(true);
    setLookupError(null);
    setLookupData(null);
    
    try {
      const response = await fetch(
        `${API}/stock-perf/data/${lookupSymbol.toUpperCase().trim()}?limit=365`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.length > 0) {
          setLookupData(data);
        } else {
          setLookupError(`No data found for symbol: ${lookupSymbol.toUpperCase()}`);
        }
      } else {
        const error = await response.json();
        setLookupError(error.detail || "Symbol not found");
      }
    } catch (error) {
      setLookupError("Failed to fetch symbol data");
    } finally {
      setLookupLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      const response = await fetch(`${API}/stock-perf/config`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(config)
      });
      if (response.ok) {
        toast.success("Configuration saved");
        setConfigDirty(false);
        fetchStats();
      } else {
        toast.error("Failed to save configuration");
      }
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const runPipeline = async (runType, extraParams = {}) => {
    if (running) {
      toast.error("Pipeline is already running");
      return;
    }

    const endpoints = {
      incremental: "/stock-perf/run/incremental",
      full_backfill: "/stock-perf/run/full-backfill",
      test: `/stock-perf/run/test?symbols_limit=${testSymbolsLimit}`,
      retry_failed: "/stock-perf/run/retry-failed",
    };

    const confirmMessages = {
      incremental: "Start daily incremental update?",
      full_backfill: "Start FULL historical backfill? This may take a long time.",
      test: `Run test with ${testSymbolsLimit} symbols?`,
      retry_failed: "Retry all failed symbols from last run?",
    };

    if (!window.confirm(confirmMessages[runType])) {
      return;
    }

    setRunning(true);
    try {
      const response = await fetch(`${API}${endpoints[runType]}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success(`${runType.replace("_", " ")} started`);
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start run");
        setRunning(false);
      }
    } catch (error) {
      toast.error("Failed to start run");
      setRunning(false);
    }
  };

  const cancelRun = async () => {
    try {
      const response = await fetch(`${API}/stock-perf/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.info("Cancel signal sent");
      }
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };

  const forceReset = async () => {
    if (!window.confirm("Force reset all stuck/stale stock performance pipelines? This will cancel any running operations.")) {
      return;
    }
    try {
      const response = await fetch(`${API}/stock-perf/force-reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Force reset complete");
        setRunning(false);
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Force reset failed");
      }
    } catch (error) {
      toast.error("Failed to force reset");
    }
  };

  const rebuildUniverse = async () => {
    try {
      const response = await fetch(`${API}/stock-perf/universe/rebuild`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Universe rebuilt: ${data.symbols_count} symbols`);
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to rebuild universe");
    }
  };

  const warmCache = async () => {
    if (!window.confirm("This will populate the last_perf_date cache for all symbols. Run this ONCE after deployment to enable fast daily incremental runs. Continue?")) {
      return;
    }
    try {
      const response = await fetch(`${API}/stock-perf/warm-cache`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Cache warm-up started in background. This may take a few minutes.");
      } else {
        toast.error("Failed to start cache warm-up");
      }
    } catch (error) {
      toast.error("Failed to start cache warm-up");
    }
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined) return "0";
    return val.toLocaleString("en-US");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const state = stats?.state || {};

  return (
    <div className="space-y-6" data-testid="stock-perf-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Stock Performance</h1>
          <p className="text-sm text-muted-foreground">
            Daily market data layer (prices, market caps, returns)
          </p>
        </div>
        <div className="flex items-center gap-2">
          {running ? (
            <>
              <Button
                variant="destructive"
                onClick={cancelRun}
                className="rounded-sm"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel Run
              </Button>
              <Button
                variant="outline"
                onClick={forceReset}
                className="rounded-sm border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                data-testid="force-reset-btn"
                title="Force cancel any stuck/stale pipelines"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Force Reset
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => runPipeline("incremental")}
                disabled={running}
                className="rounded-sm"
                data-testid="run-incremental-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Daily Update
              </Button>
              <Button
                onClick={() => runPipeline("test")}
                disabled={running}
                className="rounded-sm"
                data-testid="run-test-btn"
              >
                <TestTube className="w-4 h-4 mr-2" />
                Test Run
              </Button>
              {/* Force Reset always visible for stuck states */}
              <Button
                variant="outline"
                onClick={forceReset}
                className="rounded-sm border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                data-testid="force-reset-idle-btn"
                title="Force cancel any stuck/stale pipelines (use if pipeline appears stuck)"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Running Status Banner with Progress */}
      {running && (
        <Card className="bg-blue-500/10 border-blue-500/30 rounded-sm">
          <CardContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                <span className="text-blue-400 font-medium">
                  Pipeline running... Run ID: {state.current_run_id || "-"}
                </span>
              </div>
              
              {/* Progress details */}
              {state.progress && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Phase: <span className="text-foreground capitalize">{state.progress.current_phase?.replace(/_/g, ' ') || 'initializing'}</span>
                    </span>
                    <span className="text-blue-400 font-mono">
                      {state.progress.percent_complete || 0}%
                    </span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${state.progress.percent_complete || 0}%` }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {formatNumber(state.progress.symbols_processed || 0)} / {formatNumber(state.progress.symbols_total || 0)} symbols
                    </span>
                    <span>
                      <span className="text-green-400">{formatNumber(state.progress.symbols_succeeded || 0)} ok</span>
                      {state.progress.symbols_failed > 0 && (
                        <span className="text-red-400 ml-2">{formatNumber(state.progress.symbols_failed)} failed</span>
                      )}
                    </span>
                  </div>
                  
                  {state.progress.current_symbol && (
                    <div className="text-xs text-muted-foreground">
                      Current: <span className="font-mono text-foreground">{state.progress.current_symbol}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Universe Size</p>
                <p className="text-2xl font-bold text-primary">
                  {formatNumber(stats?.universe_count)}
                </p>
              </div>
              <Database className="w-8 h-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Symbols w/ Data</p>
                <p className="text-2xl font-bold text-green-400">
                  {formatNumber(stats?.symbols_with_data)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-400/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Daily Rows</p>
                <p className="text-2xl font-bold">
                  {formatNumber(stats?.total_rows || stats?.total_daily_rows)}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Date Range</p>
                <p className="text-sm font-mono">
                  {stats?.date_range?.oldest?.slice(0, 10) || "-"}
                </p>
                <p className="text-sm font-mono text-muted-foreground">
                  to {stats?.date_range?.newest?.slice(0, 10) || "-"}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Last Run</p>
                <Badge 
                  variant={state.last_run_status === "completed" ? "default" : 
                           state.last_run_status === "partial" ? "secondary" : "destructive"}
                  className="mt-1"
                >
                  {state.last_run_status || "never"}
                </Badge>
              </div>
              <Clock className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted rounded-sm">
          <TabsTrigger value="overview" className="rounded-sm">Overview</TabsTrigger>
          <TabsTrigger value="lookup" className="rounded-sm">
            <Search className="w-4 h-4 mr-1" />
            Lookup
          </TabsTrigger>
          <TabsTrigger value="runs" className="rounded-sm">
            <Play className="w-4 h-4 mr-1" />
            Run Controls
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-sm">
            <History className="w-4 h-4 mr-1" />
            History
          </TabsTrigger>
          <TabsTrigger value="config" className="rounded-sm">
            <Settings className="w-4 h-4 mr-1" />
            Config
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Daily Scheduler Status */}
            <Card className={`bg-card border-border rounded-sm ${schedulerState?.job_active ? 'border-green-500/30' : 'border-yellow-500/30'}`} data-testid="scheduler-status-card">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Timer className={`w-4 h-4 ${schedulerState?.job_active ? 'text-green-400' : 'text-yellow-400'}`} />
                  Daily Auto-Refresh Scheduler
                </CardTitle>
                <CardDescription>
                  Automatic daily incremental updates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    {schedulerState?.job_active ? (
                      <Badge variant="default" className="bg-green-500/20 text-green-400 border-green-500/30">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {schedulerState?.enabled ? 'Pending' : 'Disabled'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Schedule</span>
                    <span className="font-mono text-sm">
                      {String(schedulerState?.hour_utc ?? 23).padStart(2, '0')}:{String(schedulerState?.minute ?? 30).padStart(2, '0')} UTC
                    </span>
                  </div>
                  {schedulerState?.next_run_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Next Run</span>
                      <span className="text-sm text-green-400">
                        {formatDate(schedulerState.next_run_at)}
                      </span>
                    </div>
                  )}
                  {schedulerState?.last_run_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Last Run</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(schedulerState.last_run_at)}
                        </span>
                        <Badge 
                          variant={schedulerState.last_run_status === "completed" ? "default" : "destructive"}
                          className="text-xs"
                        >
                          {schedulerState.last_run_status}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Cap Distribution */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm">Market Cap Distribution</CardTitle>
                <CardDescription>Daily rows by cap bucket</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats?.cap_distribution || {}).map(([bucket, count]) => (
                    <div key={bucket} className="flex items-center justify-between">
                      <span className="text-sm capitalize">
                        {bucket === "big" ? "Big Cap (≥$1B)" : 
                         bucket === "mid_small" ? "Mid/Small ($150M-$1B)" : 
                         bucket === "micro" ? "Micro (<$150M)" : bucket || "Unknown"}
                      </span>
                      <span className="font-mono text-sm">{formatNumber(count)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Runs */}
            <Card className="bg-card border-border rounded-sm md:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm">Recent Runs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(stats?.recent_runs || []).slice(0, 5).map((run, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={run.status === "completed" ? "default" : 
                                   run.status === "partial" ? "secondary" : 
                                   run.status === "running" ? "outline" : "destructive"}
                          className="text-xs"
                        >
                          {run.status}
                        </Badge>
                        <span className="text-muted-foreground text-xs">
                          {run.run_type}
                        </span>
                      </div>
                      <span className="font-mono text-xs text-muted-foreground">
                        {formatDate(run.started_at)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Stock Lookup Tab */}
        <TabsContent value="lookup" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Search className="w-4 h-4 text-primary" />
                Stock Performance Lookup
              </CardTitle>
              <CardDescription>
                Search for a specific stock to view its daily performance data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={lookupStock} className="flex gap-2 mb-6">
                <Input
                  placeholder="Enter symbol (e.g., AAPL, MSFT, GOOGL)"
                  value={lookupSymbol}
                  onChange={(e) => setLookupSymbol(e.target.value.toUpperCase())}
                  className="max-w-xs font-mono"
                  data-testid="stock-lookup-input"
                />
                <Button 
                  type="submit" 
                  disabled={lookupLoading || !lookupSymbol.trim()}
                  className="rounded-sm"
                  data-testid="stock-lookup-btn"
                >
                  {lookupLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  <span className="ml-2">Search</span>
                </Button>
              </form>

              {lookupError && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm text-red-400 text-sm mb-4">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  {lookupError}
                </div>
              )}

              {lookupData && (
                <div className="space-y-4">
                  {/* Symbol Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Symbol</p>
                      <p className="text-lg font-bold font-mono">{lookupData.symbol}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Data Points</p>
                      <p className="text-lg font-bold">{lookupData.count}</p>
                    </div>
                    {lookupData.data[0] && (
                      <>
                        <div>
                          <p className="text-xs text-muted-foreground">Latest Price</p>
                          <p className="text-lg font-bold text-green-400">
                            ${lookupData.data[0].adj_close?.toFixed(2) || "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Sector</p>
                          <p className="text-sm font-medium">{lookupData.data[0].sector || "-"}</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Data Table */}
                  <div className="border border-border rounded-sm">
                    <div className="grid grid-cols-6 gap-2 p-2 bg-muted/50 text-xs font-medium text-muted-foreground border-b border-border">
                      <div>Date</div>
                      <div className="text-right">Adj Close</div>
                      <div className="text-right">Daily Return</div>
                      <div className="text-right">Market Cap</div>
                      <div>Cap Bucket</div>
                      <div>Industry</div>
                    </div>
                    <ScrollArea className="h-[400px]">
                      {lookupData.data.map((row, idx) => (
                        <div 
                          key={idx} 
                          className="grid grid-cols-6 gap-2 p-2 text-sm border-b border-border/50 hover:bg-muted/30"
                        >
                          <div className="font-mono text-xs">{row.date}</div>
                          <div className="text-right font-mono">
                            ${row.adj_close?.toFixed(2) || "-"}
                          </div>
                          <div className={`text-right font-mono flex items-center justify-end gap-1 ${
                            row.daily_return > 0 ? 'text-green-400' : 
                            row.daily_return < 0 ? 'text-red-400' : 'text-muted-foreground'
                          }`}>
                            {row.daily_return > 0 ? (
                              <ArrowUpRight className="w-3 h-3" />
                            ) : row.daily_return < 0 ? (
                              <ArrowDownRight className="w-3 h-3" />
                            ) : (
                              <Minus className="w-3 h-3" />
                            )}
                            {row.daily_return !== null ? `${(row.daily_return * 100).toFixed(2)}%` : "-"}
                          </div>
                          <div className="text-right font-mono text-xs">
                            {row.market_cap ? `$${(row.market_cap / 1e9).toFixed(2)}B` : "-"}
                          </div>
                          <div>
                            <Badge variant="outline" className="text-xs">
                              {row.cap_bucket || "-"}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground truncate" title={row.industry}>
                            {row.industry || "-"}
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                </div>
              )}

              {!lookupData && !lookupError && !lookupLoading && (
                <div className="text-center text-muted-foreground py-12">
                  <Search className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Enter a stock symbol to view its performance data</p>
                  <p className="text-xs mt-2">Examples: AAPL, MSFT, GOOGL, AMZN, TSLA</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Run Controls Tab */}
        <TabsContent value="runs" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Daily Incremental */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Play className="w-4 h-4 text-green-400" />
                  Daily Incremental
                </CardTitle>
                <CardDescription>
                  Fetch only new data since last update
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => runPipeline("incremental")}
                  disabled={running}
                  className="w-full rounded-sm"
                >
                  {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Run Daily Update
                </Button>
              </CardContent>
            </Card>

            {/* Test Mode */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <TestTube className="w-4 h-4 text-yellow-400" />
                  Test Mode
                </CardTitle>
                <CardDescription>
                  Run with limited symbols for testing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Symbols Limit</Label>
                    <Input
                      type="number"
                      value={testSymbolsLimit}
                      onChange={(e) => setTestSymbolsLimit(parseInt(e.target.value) || 50)}
                      className="bg-background rounded-sm"
                      min={1}
                      max={200}
                    />
                  </div>
                  <Button
                    onClick={() => runPipeline("test")}
                    disabled={running}
                    className="w-full rounded-sm"
                    variant="secondary"
                  >
                    {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                    Run Test
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Full Backfill */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-orange-400" />
                  Full Backfill
                </CardTitle>
                <CardDescription>
                  Fetch complete 8-year history for all symbols
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => runPipeline("full_backfill")}
                  disabled={running}
                  className="w-full rounded-sm"
                  variant="outline"
                >
                  {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Run Full Backfill
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Warning: This may take several hours
                </p>
              </CardContent>
            </Card>

            {/* Retry Failed */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <RotateCcw className="w-4 h-4 text-blue-400" />
                  Retry Failed
                </CardTitle>
                <CardDescription>
                  Retry symbols that failed in the last run
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => runPipeline("retry_failed")}
                  disabled={running}
                  className="w-full rounded-sm"
                  variant="outline"
                >
                  {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                  Retry Failed Symbols
                </Button>
              </CardContent>
            </Card>

            {/* Rebuild Universe */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Database className="w-4 h-4 text-purple-400" />
                  Rebuild Universe
                </CardTitle>
                <CardDescription>
                  Refresh stock universe from company profiles
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={rebuildUniverse}
                  disabled={running}
                  className="w-full rounded-sm"
                  variant="outline"
                >
                  <Database className="w-4 h-4 mr-2" />
                  Rebuild Universe
                </Button>
              </CardContent>
            </Card>

            {/* Warm Cache */}
            <Card className="bg-card border-border rounded-sm border-amber-500/30">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400" />
                  Warm Cache
                </CardTitle>
                <CardDescription>
                  One-time: Populate last_date cache for fast daily runs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={warmCache}
                  disabled={running}
                  className="w-full rounded-sm"
                  variant="outline"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Warm Cache (Run Once)
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Run this once after initial backfill to enable fast daily updates
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="text-sm">Run History</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2">Run ID</th>
                      <th className="text-left py-2">Type</th>
                      <th className="text-left py-2">Status</th>
                      <th className="text-left py-2">Started</th>
                      <th className="text-left py-2">Duration</th>
                      <th className="text-left py-2">Symbols</th>
                      <th className="text-left py-2">Rows</th>
                      <th className="text-left py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 font-mono text-xs">{run.run_id}</td>
                        <td className="py-2 text-xs">{run.run_type}</td>
                        <td className="py-2">
                          <Badge 
                            variant={run.status === "completed" ? "default" : 
                                     run.status === "partial" ? "secondary" : 
                                     run.status === "running" ? "outline" : "destructive"}
                            className="text-xs"
                          >
                            {run.status}
                          </Badge>
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {formatDate(run.started_at)}
                        </td>
                        <td className="py-2 text-xs">{formatDuration(run.duration_seconds)}</td>
                        <td className="py-2 text-xs">
                          <span className="text-green-400">{run.symbols_succeeded || 0}</span>
                          {run.symbols_failed > 0 && (
                            <span className="text-red-400 ml-1">/ {run.symbols_failed} failed</span>
                          )}
                        </td>
                        <td className="py-2 text-xs">
                          <span className="text-blue-400">+{formatNumber(run.rows_inserted || 0)}</span>
                          {run.rows_updated > 0 && (
                            <span className="text-muted-foreground ml-1">~{formatNumber(run.rows_updated)}</span>
                          )}
                        </td>
                        <td className="py-2">
                          {run.symbols_failed > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchFailedSymbols(run.run_id)}
                              className="text-xs h-6 px-2"
                            >
                              View Failed
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Failed Symbols Dialog */}
          <Dialog open={showFailedDialog} onOpenChange={setShowFailedDialog}>
            <DialogContent className="max-w-2xl max-h-[80vh]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-500" />
                  Failed Symbols ({failedSymbols.length})
                </DialogTitle>
                <DialogDescription>
                  Run ID: {selectedRunId}
                </DialogDescription>
              </DialogHeader>
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-2">
                  {failedSymbols.map((item, idx) => (
                    <div key={idx} className="flex items-start justify-between text-sm p-3 bg-red-500/10 rounded border border-red-500/20">
                      <span className="font-mono font-medium text-red-400 min-w-[80px]">{item.symbol}</span>
                      <span className="text-xs text-muted-foreground ml-4 break-all">
                        {item.error}
                      </span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Config Tab */}
        <TabsContent value="config" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm">Pipeline Configuration</CardTitle>
                  <CardDescription>Adjust pipeline behavior</CardDescription>
                </div>
                {configDirty && (
                  <Button onClick={saveConfig} className="rounded-sm">
                    Save Changes
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Concurrency */}
                <div className="space-y-2">
                  <Label>Max Workers (Concurrency)</Label>
                  <Input
                    type="number"
                    value={config.max_workers || 5}
                    onChange={(e) => {
                      setConfig({ ...config, max_workers: parseInt(e.target.value) || 5 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={1}
                    max={20}
                  />
                  <p className="text-xs text-muted-foreground">Parallel API workers (3-5 recommended for production)</p>
                </div>

                {/* Rate Limit */}
                <div className="space-y-2">
                  <Label>Calls Per Minute</Label>
                  <Input
                    type="number"
                    value={config.calls_per_minute || 2500}
                    onChange={(e) => {
                      setConfig({ ...config, calls_per_minute: parseInt(e.target.value) || 2500 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={100}
                    max={5000}
                  />
                  <p className="text-xs text-muted-foreground">FMP API rate limit</p>
                </div>

                {/* Max Retries */}
                <div className="space-y-2">
                  <Label>Max Retries</Label>
                  <Input
                    type="number"
                    value={config.max_retries || 6}
                    onChange={(e) => {
                      setConfig({ ...config, max_retries: parseInt(e.target.value) || 6 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={1}
                    max={10}
                  />
                  <p className="text-xs text-muted-foreground">Retries on transient errors</p>
                </div>

                {/* Default Lookback */}
                <div className="space-y-2">
                  <Label>Default Lookback Days</Label>
                  <Input
                    type="number"
                    value={config.default_lookback_days || 2920}
                    onChange={(e) => {
                      setConfig({ ...config, default_lookback_days: parseInt(e.target.value) || 2920 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={30}
                    max={4000}
                  />
                  <p className="text-xs text-muted-foreground">History depth for new symbols (~8 years = 2920)</p>
                </div>

                {/* Test Symbols Limit */}
                <div className="space-y-2">
                  <Label>Test Symbols Limit</Label>
                  <Input
                    type="number"
                    value={config.test_symbols_limit || 50}
                    onChange={(e) => {
                      setConfig({ ...config, test_symbols_limit: parseInt(e.target.value) || 50 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={1}
                    max={500}
                  />
                  <p className="text-xs text-muted-foreground">Symbols in test mode</p>
                </div>

                {/* Symbol Batch Size */}
                <div className="space-y-2">
                  <Label>Symbol Batch Size</Label>
                  <Input
                    type="number"
                    value={config.symbol_batch_size || 100}
                    onChange={(e) => {
                      setConfig({ ...config, symbol_batch_size: parseInt(e.target.value) || 100 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={50}
                    max={500}
                  />
                  <p className="text-xs text-muted-foreground">Symbols per memory batch (50-100 recommended for production)</p>
                </div>

                {/* Daily Refresh Hour */}
                <div className="space-y-2">
                  <Label>Daily Refresh Hour (UTC)</Label>
                  <Input
                    type="number"
                    value={config.daily_refresh_hour_utc || 23}
                    onChange={(e) => {
                      setConfig({ ...config, daily_refresh_hour_utc: parseInt(e.target.value) || 23 });
                      setConfigDirty(true);
                    }}
                    className="bg-background rounded-sm"
                    min={0}
                    max={23}
                  />
                  <p className="text-xs text-muted-foreground">Hour for automatic daily refresh</p>
                </div>
              </div>

              {/* Boolean toggles */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="flex items-center justify-between p-3 border border-border rounded-sm">
                  <div>
                    <Label>Daily Auto-Refresh</Label>
                    <p className="text-xs text-muted-foreground">Enable automatic daily updates</p>
                  </div>
                  <Switch
                    checked={config.daily_refresh_enabled ?? true}
                    onCheckedChange={(checked) => {
                      setConfig({ ...config, daily_refresh_enabled: checked });
                      setConfigDirty(true);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border border-border rounded-sm">
                  <div>
                    <Label>US/ADR Only</Label>
                    <p className="text-xs text-muted-foreground">Filter for US stocks and ADRs</p>
                  </div>
                  <Switch
                    checked={config.only_us_or_adr ?? true}
                    onCheckedChange={(checked) => {
                      setConfig({ ...config, only_us_or_adr: checked });
                      setConfigDirty(true);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border border-border rounded-sm">
                  <div>
                    <Label>Exclude ETFs</Label>
                    <p className="text-xs text-muted-foreground">Filter out ETFs from universe</p>
                  </div>
                  <Switch
                    checked={config.exclude_etf ?? true}
                    onCheckedChange={(checked) => {
                      setConfig({ ...config, exclude_etf: checked });
                      setConfigDirty(true);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border border-border rounded-sm">
                  <div>
                    <Label>Exclude Funds</Label>
                    <p className="text-xs text-muted-foreground">Filter out mutual funds</p>
                  </div>
                  <Switch
                    checked={config.exclude_fund ?? true}
                    onCheckedChange={(checked) => {
                      setConfig({ ...config, exclude_fund: checked });
                      setConfigDirty(true);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border border-border rounded-sm">
                  <div>
                    <Label>Require Active Trading</Label>
                    <p className="text-xs text-muted-foreground">Only actively trading symbols</p>
                  </div>
                  <Switch
                    checked={config.require_active_trading ?? true}
                    onCheckedChange={(checked) => {
                      setConfig({ ...config, require_active_trading: checked });
                      setConfigDirty(true);
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StockPerfPage;
